import router from "../../router/router";
import { socket, establishConnection } from "../socket.js";
import { dealCards } from "./helpers/serverMock";
import { devGame } from "./helpers/devGame";

// State
const state = {
	game: {
		id: null,
		joined: false,
		players: [],
		disconnectedPlayers: [],
		state: {
			inProgress: false,
			paused: false,
			cards: {},
			lastTaken: {},
			lastTakenPlayer: null,
			remainingValues: [],
			scores: {},
			turn: null,
			winner: null,
		},
		me: null,
	},
};

// Getters
const getters = {
	// Game object
	game: state => state.game,
	waiting: state => {
		return (
			!state.game.state.winner &&
			state.game.joined &&
			!state.game.state.inProgress
			// state.game.players.some(player => player === null)
		);
	},

	// Setup
	roomCode: state => state.game.id,
	joined: state => state.game.joined,
	values: state => state.game.settings.values,
	suits: state => state.game.settings.suits,
	players: state => state.game.players,
	disconnectedPlayers: state => state.game.disconnectedPlayers,
	me: state => state.game.me,

	// Game state
	turn: state => state.game.state.turn,
	scores: state => state.game.state.scores,
	cardsArray: state => state.game.state.cards,
	myCardsArray: state => state.game.state.cards[state.game.me],

	// Component getters
	winningTeam: state => state.game.state.winner,
	lastTaken: state => state.game.state.lastTaken,
	lastTakenPlayer: state => state.game.state.lastTakenPlayer,

	// Computed
	teams: state => {
		const teams = { 0: {}, 1: {} };
		for (let team in teams) {
			// Assign scores[0] to teams[0].score
			teams[team].score = state.game.state.scores[team];

			// Split four players into two teams, then create an array of names
			teams[team].players = state.game.players
				.filter(p => p.pos % 2 === parseInt(team)) //
				.map(p => p.name); //
		}
		return teams;
	},
};

// Actions
const actions = {
	// Socket server
	tryConnect({ dispatch, getters }) {
		return new Promise(res => {
			if (getters.mockingServer) res();
			else
				establishConnection()
					.then(res())
					.catch(() => {
						dispatch(
							"showPopupError",
							"Couldn't establish connection to server."
						);
					});
		});
	},
	tryDisconnect() {
		return new Promise(res => {
			if (!getters.mockingServer) {
				socket.close();
			}
			res();
		});
	},

	// Joining game
	tryJoinGame({ dispatch, getters }, { aRoomCode, aName }) {
		if (!getters.mockingServer) {
			dispatch("tryConnect").then(() => {
				socket.emit("player-join-game", aRoomCode, aName);
			});
		} else {
			console.log("Mocking tryJoinGame...");
			dispatch("devStartGame", aRoomCode);
			dispatch("joinedGame", {
				aGame: getters.game,
				aPlayer: getters.game.players[0],
			});
		}
	},
	tryReconnectToGame({ dispatch, getters }, { aRoomCode, aLocalId }) {
		!getters.mockingServer
			? dispatch("tryConnect").then(() => {
					socket.emit("player-reconnect", aRoomCode, aLocalId);
			  })
			: dispatch("playerReconnected", 4);
	},
	tryLeaveGame({ dispatch }) {
		dispatch("tryDisconnect").then(dispatch("leftGame"));
	},
	tryCreateNewGame({ dispatch, getters }) {
		if (!getters.mockingServer) {
			dispatch("tryConnect").then(() => {
				socket.emit("get-new-game");
				socket.once("send-new-game-code", aRoomCode => {
					router.push(`/invite/${aRoomCode}`);
				});
			});
		} else {
			const aRoomCode = Math.floor(Math.random() * 10000).toString();
			router.push(`/invite/${aRoomCode}`);
		}
	},

	// Gameplay
	tryCheckGuess({ dispatch, getters }, { value, suits }) {
		if (!getters.mockingServer) {
			getters.forceDevPlay
				? socket.emit("dev-check-guess", value)
				: socket.emit("player-check-guess", value, suits);
		} else {
			console.log("Mocking tryCheckGuess...");
			if (getters.game.state.remainingValues.includes(value)) {
				dispatch("playRemoveValue", value);
				dispatch("playAddPoint");
			}
		}
	},
	tryPassTurn({ dispatch, getters }) {
		if (!getters.mockingServer) {
			getters.forceDevPlay
				? socket.emit("dev-pass-turn")
				: socket.emit("player-pass-turn");
		} else {
			// console.log("Mocking playPassTurn...");
			dispatch("playPassTurn");
		}
	},
	tryRefreshState({ getters }) {
		if (!getters.mockingServer) {
			socket.emit("refresh-state");
		}
	},
	tryStartNewGame({ dispatch, getters }) {
		if (!getters.mockingServer) {
			socket.emit("start-new-game");
		} else {
			dispatch("devStartGame");
		}
	},

	// 2. --- both, READY TO COMMIT

	// Client joining game
	joinedGame({ commit, dispatch, getters }, { aGame, aPlayer }) {
		console.log("joinedGame called...", aGame.state, aPlayer);
		// Game info
		commit("SET_JOINED_GAME", true);
		commit("SET_GAME_PROGRESS", aGame.state.inProgress);
		commit("SET_GAME_ROOM_CODE", aGame.id);
		commit("SET_GAME_SETTINGS", aGame.settings);
		commit("SET_PLAYERS", aGame.players);
		// Player info
		commit("SET_ME", aPlayer.pos);
		// User/UI
		commit("USER_SET_NAME", aPlayer.pos);
		commit("ADD_LOG_MESSAGE", `Joined game ${aGame.id}`);
		router.push(`/game/${aGame.id}`);
		if (getters.mockingServer && readyToStart(aGame)) {
			dispatch("playStartGame", aGame.state);
		}
	},
	leftGame({ commit }) {
		console.log("Left successfully.");
		commit("SET_LEAVE_GAME");
		commit("ADD_LOG_MESSAGE", "Left successfully.");
	},

	// Other players
	playerJoined({ commit }, aPlayer) {
		commit("SET_ADD_PLAYER", aPlayer);
	},
	playerLeft({ commit }, aPlayerPos) {
		commit("SET_REMOVE_PLAYER", aPlayerPos);
	},
	playerDisconnected({ commit }, aPlayerPos) {
		commit("SET_PLAYER_DISCONNECTED", aPlayerPos);
		commit("ADD_LOG_MESSAGE", "Player has disconnected.");
	},
	playerReconnected({ commit }, aPlayerPos) {
		commit("SET_PLAYER_RECONNECTED", aPlayerPos);
	},

	// Game starting
	playStartGame({ commit }, aGameState) {
		console.log("playStartGame called...");
		// Start game
		commit("PLAY_START_GAME");
		// Turn and scores
		commit("PLAY_SET_TURN", aGameState.turn);
		commit("PLAY_SET_SCORES", aGameState.scores);
		// Reset card stuff
		commit("PLAY_RESET_REMAINING_VALUES");
		commit("PLAY_RESET_LAST_TAKEN");
		// console.log("CARDS WE HAVE", aGameState.cards);
		commit("PLAY_SET_CARDS", prepareCards(aGameState.cards));
	},

	// Gameplay
	playPassTurn({ commit }) {
		commit("PLAY_PASS_TURN");
	},
	playSetCards({ commit }, cards) {
		commit("PLAY_SET_CARDS", cards);
	},
	playAddPoint({ commit }, toTeam) {
		commit("PLAY_ADD_POINT", toTeam);
	},
	playRemoveValue({ commit }, aValue) {
		commit("PLAY_REMOVE_VALUE", aValue);
	},
	playAnnounceWinner({ commit }) {
		commit("PLAY_ANNOUNCE_WINNER");
		// commit("PLAY_END_GAME");
	},
	playRefreshState({ commit }, aGameState) {
		commit("PLAY_SET_TURN", aGameState.turn);
		commit("PLAY_SET_SCORES", aGameState.scores);
		commit("PLAY_SET_CARDS", prepareCards(aGameState.cards));
	},

	// Development
	devStartGame({ commit }, aRoomCode) {
		commit("DEV_ACTIVATE_DEV_GAME", aRoomCode);
		commit("SET_GAME_STARTED");
		commit("PLAY_RESET_REMAINING_VALUES");
		commit("PLAY_RESET_LAST_TAKEN");
		commit("PLAY_SET_CARDS", prepareCards(dealCards(state.game)));
	},
	devController({ commit }, controller) {
		commit(controller);
	},
	devTestEvent(context, ...args) {
		if (socket.connected) {
			socket.emit("test-event", ...args);
		}
	},
};

// Mutations
const mutations = {
	// When joining a game
	SET_JOINED_GAME: (state, aBoolean) => {
		state.game.joined = aBoolean;
	},
	SET_LEAVE_GAME: state => {
		state.game.joined = false;
	},
	SET_GAME_ROOM_CODE: (state, aRoomCode) => {
		state.game.id = aRoomCode;
	},
	SET_GAME_OBJECT: (state, aGame) => {
		state.game.players = aGame.players;
		state.game.settings = aGame.settings;
		state.game.state = aGame.state;
	},
	SET_GAME_SETTINGS: (state, gameSettings) => {
		state.game.settings = gameSettings;
	},
	SET_GAME_PROGRESS: (state, aBoolean) => {
		state.game.state.inProgress = aBoolean;
	},
	SET_GAME_STARTED: state => {
		state.game.state.inProgress = true;
		state.game.state.winner = null;
	},
	SET_PLAYERS: (state, playersArr) => {
		state.game.players = playersArr;
	},
	SET_ADD_PLAYER: (state, aPlayer) => {
		state.game.players[aPlayer.pos] = aPlayer;
	},
	SET_REMOVE_PLAYER: (state, aPlayerPos) => {
		state.game.players[aPlayerPos] = null;
	},
	SET_PLAYER_DISCONNECTED: (state, aPlayerPos) => {
		if (!state.game.disconnectedPlayers.includes(aPlayerPos)) {
			state.game.disconnectedPlayers.push(aPlayerPos);
		}
	},
	SET_PLAYER_RECONNECTED: (state, aPlayerPos) => {
		let index = state.game.disconnectedPlayers.indexOf(aPlayerPos);
		if (index !== -1) {
			state.game.disconnectedPlayers.splice(index, 1);
		}
	},
	SET_ME: (state, aMe) => {
		state.game.me = aMe;
	},

	// When game starting

	// Game started
	PLAY_START_GAME: state => {
		state.game.state.inProgress = true;
		state.game.state.winner = null;
	},
	PLAY_END_GAME: state => {
		state.game.state.inProgress = false;
	},
	PLAY_PAUSE_GAME: state => {
		state.game.state.paused = true;
	},
	PLAY_CONTINUE_GAME: state => {
		state.game.state.paused = false;
	},
	PLAY_SET_TURN: (state, turn) => {
		state.game.state.turn = turn;
	},
	PLAY_SET_SCORES: (state, scores) => {
		state.game.state.scores = scores;
	},
	PLAY_RESET_REMAINING_VALUES: state => {
		state.game.state.remainingValues = [...state.game.settings.values];
	},
	PLAY_RESET_LAST_TAKEN: state => {
		state.game.players.forEach(p => {
			state.game.state.cards[p.pos] = [];
			state.game.state.lastTaken[p.pos] = []; // Otherwise first animation is broken
		});
	},
	PLAY_SET_CARDS: (state, preparedCards) => {
		state.game.state.cards = preparedCards;
	},

	// During game
	PLAY_ADD_POINT: (state, aTeam = currentTeam(state.game)) => {
		state.game.state.scores[aTeam]++;
	},
	PLAY_PASS_TURN: state => {
		state.game.state.turn =
			(state.game.state.turn + 1) % state.game.players.length;
	},
	PLAY_REMOVE_VALUE: (state, aValue) => {
		// Remove from array
		state.game.state.remainingValues = state.game.state.remainingValues.filter(
			value => value !== aValue
		);

		// Reset lastTaken
		state.game.state.lastTaken = {};
		state.game.state.lastTakenPlayer = state.game.state.turn;

		// Go through each player Pos
		for (let pos in state.game.state.cards) {
			// Add cards to lastTaken
			state.game.state.cards[pos].forEach(card => {
				if (!state.game.state.lastTaken[pos]) {
					state.game.state.lastTaken[pos] = [];
				}
				if (card.value == aValue) {
					state.game.state.lastTaken[pos].push(card);
				}
			});
			state.game.state.cards[pos] = state.game.state.cards[pos].filter(
				card => card.value != aValue
			);
		}
	},
	PLAY_ANNOUNCE_WINNER: state => {
		// state.game.state.inProgress = false;
		state.game.state.winner = state.game.state.turn % 2;
	},

	// Development stuff
	DEV_ACTIVATE_DEV_GAME: (state, aRoomCode) => {
		const newGame = new devGame(aRoomCode);
		state.game = newGame.game;
		console.log(state.game);
	},
	DEV_TOGGLE_INPROGRESS: state => {
		state.game.state.inProgress = !state.game.state.inProgress;
		state.game.state.winner = null;
	},
};

const currentTeam = aGame => {
	return aGame.state.turn % 2;
};

const prepareCards = serverCards => {
	let preparedCards = { 0: [], 1: [], 2: [], 3: [] };
	let count = 0;
	for (let value in serverCards) {
		for (let suit in serverCards[value]) {
			preparedCards[serverCards[value][suit]].push({
				value: value,
				suit: suit,
				id: count,
			});
			count++;
		}
	}
	return preparedCards;
};

function readyToStart(aGame) {
	return (
		aGame.players.length === aGame.settings.numPlayers &&
		aGame.players.every(player => player !== null)
	);
}

export default {
	state,
	getters,
	actions,
	mutations,
};
